import React, { useMemo, Fragment, } from 'react';
import { omit, last, isEmpty, orderBy, sortBy, keyBy, pick, omitBy, isUndefined, isEqual, } from 'lodash';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import { format as formatDate, startOfDay, endOfDay, parse as parseDate, } from 'date-fns';
import numeral from 'numeral';

import firebase, { functions } from '../../firebase';
import { fetchPdf, } from '../../util';
import { colors, } from '../../shared/config';
import { canUpdatePurchaseOrder } from '../../shared/abilities';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import PurchaseOrderFormModal from '../modals/PurchaseOrderFormModal';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useQueryParams from '../hooks/useQueryParams';
import useSimulation from '../hooks/useSimulation';
import useFirebaseUser from '../hooks/useFirebaseUser';
import AddInTenantButton from '../AddInTenantButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ImportButton from '../ImportButton';
import ExportButton from '../ExportButton';
import ModalButton from '../ModalButton';
import QuerySortLabel from '../QuerySortLabel';
import AdminHelp from '../AdminHelp';
import InventoriesHeader from '../InventoriesHeader';
import { fields, computeAlerts, computeLeftQuantities, } from '../../shared/models/purchaseOrder';
import QuerySelector from '../QuerySelector';
import QueryDateRangeSelector from '../QueryDateRangeSelector';
import PurchaseOrderContent from '../PurchaseOrderContent';
import ProgressButton from '../ProgressButton';

const { keys } = Object;
const db = firebase.firestore();
const storageRef = firebase.storage().ref();
const sendPurchaseOrder = functions.httpsCallable('sendPurchaseOrder');

const exportFields = omit(fields({ suppliers: [] }), ['orderedOn']);
const exportFieldNames = Object.keys(exportFields);
const dateFieldNames = exportFieldNames.filter(_ => exportFields[_].type === 'date');
const confirmableDateFieldNames = dateFieldNames.filter(_ => exportFields[_].confirmable);
const fieldLabel = (name) => exportFields[name]?.label || name;
const parseImportDate = (v) => {
  if (!v) return null;
  const parsed = parseDate(v, 'yyyy/MM/dd', new Date());
  return isNaN(parsed.getTime()) ? null : firebase.firestore.Timestamp.fromDate(parsed);
};

const computedOnlyFields = ['computedItems', 'filteredItems'];

const poTableColumns = [
  { label: '仕入先', minWidth: 130 },
  { label: '発注日', minWidth: 110, field: 'orderedOn' },
  { label: 'PI受信日', minWidth: 110, field: 'piReceivedOn' },
  { label: '前金支払日', minWidth: 110, field: 'advancePaymentOn' },
  { label: '製造日', minWidth: 110, field: 'scheduledManufactureDate' },
  { label: '色指定日', minWidth: 110, field: 'colorSpecificationDate' },
  { label: 'タイヤ入荷日', minWidth: 120, field: 'tireArrivalDate' },
  { label: 'フレーム入荷日', minWidth: 130, field: 'frameArrivalDate' },
  { label: 'その他パーツ入荷日', minWidth: 150, field: 'otherPartsArrivalDate' },
  { label: '完成日', minWidth: 110, field: 'completedOn' },
  { label: 'オンライン検品日', minWidth: 130, field: 'onlineInspectionDate' },
  { label: 'オンライン検品結果', minWidth: 250 },
  { label: '残金支払日', minWidth: 110, field: 'balancePaymentOn' },
  { label: '工場出荷希望日', minWidth: 130, field: 'requestEtd' },
  { label: '備考欄', minWidth: 250 },
  { label: 'メモ', minWidth: 250 },
  { label: '商品コード', minWidth: 100 },
  { label: '製造数', minWidth: 70 },
  { label: 'コンテナ', minWidth: 100 },
  { label: '残数', minWidth: 70 },
];

const addOperationLog = async (tenantId, poRef, operationType, after, operatedBy) => {
  await db.collection('operationLogs').add({
    tenantId,
    purchaseOrderId: poRef.id,
    operationType,
    operatedAt: new Date(),
    operatedBy: omitBy(pick(operatedBy, ['uid', 'email', 'displayName']), isUndefined),
    after: omit(after, computedOnlyFields),
  });
};

export default AdminPage(function AdminInventoriesSchedule (props) {
  const { tenant, user } = props;
  const { firebaseUser } = useFirebaseUser();
  const queryParams = useQueryParams();
  const { simulationId, isSimulating, isEditable, isMasterLocked, withChanges, changeOf, recordChange, removeChange, } = useSimulation();
  // シミュレーション中はマスタを書き換えず変更内容を記録する。
  // マスタ側は有効なシミュレーションがある間ロックされる
  const canEdit = canUpdatePurchaseOrder(user) && (isSimulating ? isEditable : !isMasterLocked);
  // シミュレーション中は変更取消ボタンが増えるぶん操作列を広げる
  const labelColWidth = isSimulating ? 113 : 83;
  const scheduledManufactureDateStartOn = useMemo(_ => queryParams.scheduledManufactureDateRange?.[0] ? startOfDay(new Date(queryParams.scheduledManufactureDateRange[0])) : null, [queryParams.scheduledManufactureDateRange]);
  const scheduledManufactureDateEndOn = useMemo(_ => queryParams.scheduledManufactureDateRange?.[1] ? endOfDay(new Date(queryParams.scheduledManufactureDateRange[1])) : null, [queryParams.scheduledManufactureDateRange]);
  const productTypes = useCollectionSubscriptionInTenant(db.collection('productTypes').orderBy('index'));
  const productTypesById = keyBy(productTypes, 'id');
  const productTypeOptions = productTypes.map(_ => ({ value: _.id, label: _.name }));
  const products = useCollectionSubscriptionInTenant(db.collection('products').orderBy('code'));
  const poProducts = products.filter(_ => _.isBody || _.isOption);
  const productOptions = poProducts.map(_ => ({ value: _.id, label: _.code }));
  const productsById = keyBy(products, 'id');
  const suppliers = sortBy(useCollectionSubscriptionInTenant(db.collection('suppliers')), 'name');
  const suppliersById = keyBy(suppliers, 'id');
  const supplierOptions = suppliers.map(_ => ({ value: _.id, label: _.name }));
  const cellNotes = useCollectionSubscriptionInTenant(db.collection('cellNotes'));
  const cellNotesById = useMemo(_ => keyBy(cellNotes, 'id'), [cellNotes]);
  const { items: _purchaseOrders, isLoading: isLoadingPurchaseOrders } = useCollectionSubscriptionInTenant(db.collection('purchaseOrders'), [], { detail: true });
  const purchaseOrders = useMemo(_ => {
    const _withChanges = withChanges(_purchaseOrders, 'purchaseOrders');
    const sortField = queryParams.sort?.field ?? 'poNumber';
    const sortDirection = queryParams.sort?.direction ?? 'desc';
    const isDateField = poTableColumns.some(_ => _.field === sortField);
    return isDateField
      ? orderBy(_withChanges, _ => _[sortField]?.toMillis?.() ?? Infinity, sortDirection)
      : orderBy(_withChanges, _ => _[sortField], sortDirection);
  }, [_purchaseOrders, withChanges, queryParams.sort?.field, queryParams.sort?.direction]);
  const __containers = useCollectionSubscriptionInTenant(db.collection('containers'));
  const _containers = useMemo(_ => withChanges(__containers, 'containers'), [__containers, withChanges]);
  const containers = useMemo(_ => orderBy(_containers, _ => _.arrivalDate.toDate()), [_containers]);
  const computedPurchaseOrders = useMemo(_ => computeLeftQuantities(purchaseOrders, containers), [purchaseOrders, containers]);
  const suppliersByName = keyBy(suppliers, 'name');
  const purchaseOrdersByPoNumber = keyBy(purchaseOrders, 'poNumber');

  const exportRows = useMemo(_ => purchaseOrders.map(po => ({
    [fieldLabel('poNumber')]: po.poNumber,
    [fieldLabel('supplierId')]: suppliersById[po.supplierId]?.name || '',
    ...Object.fromEntries(dateFieldNames.map(f => [fieldLabel(f), po[f] ? formatDate(po[f].toDate(), 'yyyy/MM/dd') : ''])),
    [fieldLabel('onlineInspectionResult')]: po.onlineInspectionResult || '',
    [fieldLabel('info')]: po.info || '',
    [fieldLabel('note')]: po.note || '',
  })), [purchaseOrders, suppliersById]);

  const processImportRow = async (batch, row) => {
    const poNumber = row[fieldLabel('poNumber')];
    if (!poNumber) return;

    const supplierName = row[fieldLabel('supplierId')];
    const supplier = suppliersByName[supplierName];
    const existing = purchaseOrdersByPoNumber[poNumber];
    const ref = existing ? existing.ref : db.collection('purchaseOrders').doc();

    const parsedDates = Object.fromEntries(dateFieldNames.map(f => [f, parseImportDate(row[fieldLabel(f)])]).filter(([, v]) => v !== null));
    const now = new Date();
    const confirmedFields = {
      ...(existing?.confirmedFields || {}),
      ...Object.fromEntries(confirmableDateFieldNames.filter(f => parsedDates[f] && parsedDates[f].toDate() < now).map(f => [f, true])),
    };

    const data = {
      ...(!existing && { tenantId: tenant.id, items: [] }),
      poNumber,
      ...(supplier && { supplierId: supplier.id }),
      ...parsedDates,
      confirmedFields,
      ...(row[fieldLabel('onlineInspectionResult')] && { onlineInspectionResult: row[fieldLabel('onlineInspectionResult')] }),
      ...(row[fieldLabel('info')] && { info: row[fieldLabel('info')] }),
      ...(row[fieldLabel('note')] && { note: row[fieldLabel('note')] }),
    };

    batch.set(ref, data, { merge: true });
    const logRef = db.collection('operationLogs').doc();
    batch.set(logRef, {
      tenantId: tenant.id,
      purchaseOrderId: ref.id,
      operationType: 'import',
      operatedAt: new Date(),
      operatedBy: omitBy(pick(firebaseUser, ['uid', 'email', 'displayName']), isUndefined),
      after: data,
    });
  };

  let filteredRowGroups = computedPurchaseOrders;
  if(scheduledManufactureDateStartOn != null) {
    console.log(scheduledManufactureDateStartOn);
    filteredRowGroups = filteredRowGroups.filter(_ => _.scheduledManufactureDate?.toDate() >= scheduledManufactureDateStartOn);
  }
  if(scheduledManufactureDateEndOn != null) {
    filteredRowGroups = filteredRowGroups.filter(_ => _.scheduledManufactureDate?.toDate() <= scheduledManufactureDateEndOn);
  }
  if(!isEmpty(queryParams.supplierIds)) {
    filteredRowGroups = filteredRowGroups.filter(_ => queryParams.supplierIds.includes(_.supplierId));
  }
  filteredRowGroups = filteredRowGroups.map((rowGroup) => {
    let filteredItems = rowGroup.computedItems;
    if(!isEmpty(queryParams.productTypeIds)) {
      filteredItems = filteredItems.filter(_ => productsById[_.productId]?.productTypeIds?.some(_ => queryParams.productTypeIds.includes(_)));
    }
    if(!isEmpty(queryParams.productIds)) {
      filteredItems = filteredItems.filter(_ => queryParams.productIds.includes(_.productId));
    }
    return {
      ...rowGroup,
      filteredItems,
    };
  }).filter(_ => isEmpty(queryParams.productTypeIds) && isEmpty(queryParams.productIds) || !isEmpty(_.filteredItems));

  return (
    <div>
      <div className="admin-purchase-orders container-fluid py-5 position-relative bg-white">
        {
          false && computedPurchaseOrders[0] && (
            <div>
              <div className="d-flex justify-content-center hoge">
                <div className="d-flex justify-content-center" style={{ width: 1260, }}>
                  <PurchaseOrderContent purchaseOrder={last(computedPurchaseOrders)} supplier={suppliersById[last(computedPurchaseOrders)?.supplierId]} products={products} productsById={productsById} />
                </div>
              </div>
            </div>
          )
        }
        <InventoriesHeader activeTab="purchaseOrders" />
        <div className='mt-2 d-flex align-items-end flex-wrap gap-2 position-relative' style={{ zIndex: 101, }}>
          <QueryDateRangeSelector label="製造日" defaultValue={[scheduledManufactureDateStartOn, scheduledManufactureDateEndOn]} paramName="scheduledManufactureDateRange" pickerProps={{ isClearable: true, showYearDropdown: true, dropdownMode: 'select' }} />
          <QuerySelector paramName="supplierIds" width={300} isMulti options={supplierOptions} label="仕入先で絞込み" />
          <QuerySelector paramName="productTypeIds" width={400} isMulti options={productTypeOptions} label="商品種別で絞込み" />
          <QuerySelector paramName="productIds" width={300} isMulti options={productOptions} label="商品で絞込み" />
        </div>
        <div className="mt-4 d-flex justify-content-end gap-1 mb-3 position-relative" style={{ zIndex: 100, }}>
          <ImportButton processRow={processImportRow} disabled={!canEdit || isSimulating || isLoadingPurchaseOrders} />
          <ExportButton fileName="purchase_orders.csv" rows={exportRows} user={user} />
          {
            isSimulating ? (
              <ModalButton
                color="primary"
                label="追加"
                icon={<span className="fas fa-plus mr-1" />}
                Modal={PurchaseOrderFormModal}
                modalProps={({ toggleModal }) => ({
                  values: {},
                  products: poProducts,
                  suppliers,
                  // シミュレーション上の新規PO。IDはクライアント採番し、マスタには書き込まない
                  onSubmit: async (values) => {
                    await recordChange('purchaseOrders', 'create', db.collection('purchaseOrders').doc().id, values);
                    toast.success('シミュレーションに追加しました');
                    toggleModal(false);
                  },
                })}
                disabled={!canEdit}
              />
            ) : (
              <AddInTenantButton
                itemRef={db.collection('purchaseOrders').doc()}
                FormModal={PurchaseOrderFormModal}
                formProps={{ products: poProducts, suppliers, }}
                disabled={!canEdit}
                onFinish={async ({ ref, ...values }) => {
                  await addOperationLog(tenant.id, ref, 'create', omit(values, ['id']), firebaseUser);
                }}
              />
            )
          }
        </div>
        <div className="overflow-auto" style={{ maxHeight: '80vh', zIndex: 0, }}>
          {
            purchaseOrders.length > 0 ? (
              <table className="table table-bordered table-sm sticky-table m-0">
                <thead className="thead-light text-center">
                  <tr>
                    <th style={{ minWidth: labelColWidth }} className="sticky bg-light"></th>
                    <th className="sticky bg-light" style={{ left: labelColWidth, minWidth: 100 }}>
                      <QuerySortLabel field="poNumber" defaultActive defaultDirection="asc">
                        PO No.
                      </QuerySortLabel>
                    </th>
                    {poTableColumns.map(_ => <th key={_.label} style={{ minWidth: _.minWidth }}>{_.field ? <QuerySortLabel field={_.field} defaultDirection="desc">{_.label}</QuerySortLabel> : _.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {
                    filteredRowGroups.map((computedPurchaseOrder) => {
                      const { id, ref, } = computedPurchaseOrder;
                      const supplier = suppliersById[computedPurchaseOrder.supplierId];
                      return (
                        <PurchaseOrderRows
                          key={id}
                          labelColWidth={labelColWidth}
                          purchaseOrder={computedPurchaseOrder}
                          supplier={supplier}
                          productsById={productsById}
                          queryParams={queryParams}
                          alerts={computeAlerts(computedPurchaseOrder, supplier)}
                          user={user}
                          cellNotesById={cellNotesById}
                          labelCell={
                            <div>
                              <div className="d-flex gap-1">
                                <EditButton
                                  size="sm"
                                  label=""
                                  itemRef={ref ?? db.collection('purchaseOrders').doc(id)}
                                  FormModal={PurchaseOrderFormModal}
                                  formProps={{ products: poProducts, suppliers, }}
                                  disabled={!canEdit}
                                  beforeEdit={_ => isSimulating ? computedPurchaseOrder : _}
                                  {...(
                                    isSimulating ? {
                                      // マスタは書き換えず、変更内容だけを記録する
                                      onSubmit: async (values, { toggleModal, }) => {
                                        await recordChange('purchaseOrders', computedPurchaseOrder.isSimulationCreated ? 'create' : 'update', id, omit(values, ['id', 'ref']));
                                        toast.success('シミュレーションに記録しました');
                                        toggleModal?.(false);
                                      },
                                    } : {
                                      onFinish: async (values) => {
                                        await addOperationLog(tenant.id, ref, 'update', omit(values, ['id', 'ref']), firebaseUser);
                                      },
                                    }
                                  )}
                                />
                                <DeleteButton
                                  size="sm"
                                  label=""
                                  itemRef={ref ?? db.collection('purchaseOrders').doc(id)}
                                  disabled={!canEdit}
                                  beforeDelete={
                                    isSimulating ? (async _ => {
                                      await recordChange('purchaseOrders', 'delete', id);
                                      toast.success('シミュレーション上で削除しました');
                                      return false;
                                    }) : undefined
                                  }
                                />
                                {
                                  isSimulating && changeOf('purchaseOrders', id) && (
                                    <ProgressButton
                                      size="sm"
                                      color="link"
                                      className="p-1"
                                      title="この変更を取り消す"
                                      process={_ => window.confirm('この変更を取り消しますか？') && removeChange('purchaseOrders', id)}
                                      disabled={!isEditable}
                                    >
                                      <span className="fas fa-undo" />
                                    </ProgressButton>
                                  )
                                }
                              </div>
                              <div className="d-flex gap-1 mt-1">
                                <ModalButton size="sm" title="PO" modalProps={{ style: { minWidth: 1260, } }} content={_ => <PurchaseOrderModalContent {...{ purchaseOrder: computedPurchaseOrder, supplier, products, productsById, ..._, }} />}>
                                  <span className="fas fa-paper-plane" />
                                </ModalButton>
                                <ModalButton size="sm" title="変更履歴" modalProps={{ style: { minWidth: '90vw', } }} content={_ => <PurchaseOrderOperationLogsContent {...{ purchaseOrder: computedPurchaseOrder, suppliersById, productsById, ..._, }} />}>
                                  <span className="fas fa-clock" />
                                </ModalButton>
                              </div>
                            </div>
                          }
                        />
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                POは未登録です
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});

function PurchaseOrderModalContent (props) {
  const { purchaseOrder, supplier, products, productsById, toggleModal, } = props;
  const onSubmit = async (values) => {
    if(!window.confirm('送信します。よろしいですか？')) return;

    try {
      const pageNode = document.querySelector('html').cloneNode(true);
      pageNode.querySelector('body').innerHTML = document.querySelector('.po-container').innerHTML;
      const pdf = await fetchPdf(pageNode.outerHTML, { printBackground: true });
      const fileRef = storageRef.child(`purchaseOrders/${purchaseOrder.id}/${new Date().getTime()}.pdf`);
      await fileRef.put(pdf, { contentType: 'application/pdf', });
      const fileUrl = await fileRef.getDownloadURL();
      await sendPurchaseOrder({ purchaseOrderId: purchaseOrder.id, fileUrl, ...values, });
      toast.success('送信しました');
      toggleModal(false);
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };
  const emailFields = {
    poEmailSubject: {
      type: 'string',
      label: 'メールタイトル',
      initialValue: purchaseOrder.poNumber,
    },
    poEmailBody: {
      type: 'text',
      label: 'メール本文',
    },
  };

  return (
    <div>
      <div className="mb-1 d-flex justify-content-end align-items-center gap-1">
        <ModalButton title="送信履歴" modalProps={{ style: { minWidth: '70vw', } }} content={_ => <PurchaseOrderLogsContent {...{ purchaseOrder, products, productsById, ..._, }} />}>
          <span className="fas fa-history mr-1" />
          送信履歴
        </ModalButton>
        <ModalButton Modal={ModelFormModal} modalProps={{ title: 'PO送信', fields: emailFields, onSubmit, submitLabel: '送信', }}>
          送信
        </ModalButton>
      </div>
      <div className="d-flex justify-content-center po-container">
        <div className="d-flex justify-content-center p-4" style={{ width: 1260, }}>
          <PurchaseOrderContent {...{ purchaseOrder, supplier, products, productsById, }} />
        </div>
      </div>
    </div>
  );
}

const operationTypeLabel = (type) => ({ create: '新規作成', update: '編集', import: 'インポート' })[type] ?? type;

function PurchaseOrderOperationLogsContent (props) {
  const { purchaseOrder, suppliersById, productsById, } = props;
  const operationLogs = orderBy(
    useCollectionSubscriptionInTenant(db.collection('operationLogs').where('purchaseOrderId', '==', purchaseOrder.id), [purchaseOrder.id]),
    _ => _.operatedAt?.toDate?.() ?? new Date(0),
    'desc',
  );

  return (
    <div className="overflow-auto" style={{ maxHeight: '80vh' }}>
      <table className="table table-bordered table-sm sticky-table m-0">
        <thead className="thead-light text-center">
          <tr>
            <th style={{ minWidth: 83, maxWidth: 83 }} className="sticky bg-light">操作</th>
            <th className="sticky bg-light" style={{ left: 83, minWidth: 100 }}>PO No.</th>
            {poTableColumns.map(_ => <th key={_.label} style={{ minWidth: _.minWidth }}>{_.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {operationLogs.map((log, i) => {
            const { operationType, operatedAt, operatedBy, after } = log;
            const prevAfter = operationLogs[i + 1]?.after;
            const labelCell = (
              <div>
                <div><span className="badge badge-secondary">{operationTypeLabel(operationType)}</span></div>
                <div className="small text-muted mt-1">{operatedAt ? formatDate(operatedAt.toDate(), 'yyyy/MM/dd HH:mm') : ''}</div>
                <div className="small">{operatedBy?.displayName ?? operatedBy?.email ?? ''}</div>
              </div>
            );
            return after && (
              <PurchaseOrderRows key={i} purchaseOrder={{ ...after, id: `log-${i}`, items: after.items ?? [] }} supplier={suppliersById[after.supplierId]} productsById={productsById} queryParams={{}} labelCell={labelCell} prevAfter={prevAfter} />
            );
          })}
        </tbody>
      </table>
      {operationLogs.length === 0 && <div className="text-muted p-3">変更履歴はありません</div>}
    </div>
  );
}

function PurchaseOrderLogsContent (props) {
  const { purchaseOrder, products, productsById, toggleModal, } = props;

  return (
    <div>
      <table className="table">
        <thead className="thead-light">
          <tr>
            <th>
              送信日時
            </th>
            <th>
              送信者
            </th>
            <th>
              ファイル
            </th>
          </tr>
        </thead>
        <tbody>
          {
            (purchaseOrder.sendingLogs ?? []).map((sendingLog, i) => {
              const { sentAt, sentBy, name, fileUrl, } = sendingLog;
              return (
                <tr key={i}>
                  <td>
                    {formatDate(sentAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}
                  </td>
                  <td>
                    {sentBy?.displayName}
                  </td>
                  <td>
                    <a target="_blank" href={fileUrl}>
                      {name}.pdf
                    </a>
                  </td>
                </tr>
              );
            })
          }
          <tr>
            
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const isChanged = (a, b) => {
  if (a?.toMillis && b?.toMillis) return a.toMillis() !== b.toMillis();
  return !isEqual(a, b);
};

function PurchaseOrderRows (props) {
  const { purchaseOrder, supplier, productsById, queryParams, labelCell, prevAfter, alerts = [], user, cellNotesById = {}, labelColWidth = 83, } = props;
  const { id, poNumber, supplierId, orderedOn, piReceivedOn, advancePaymentOn, scheduledManufactureDate, colorSpecificationDate, tireArrivalDate, frameArrivalDate, otherPartsArrivalDate, completedOn, onlineInspectionDate, onlineInspectionResult, balancePaymentOn, requestEtd, confirmedFields = {}, filteredItems, computedItems, items = [], info, note, } = purchaseOrder;
  const dateColor = (fieldName) => confirmedFields[fieldName] ? undefined : '#28a745';
  const alertBg = (fieldName) => (alerts.includes(fieldName) || alerts.includes(`${fieldName}:unconfirmed`)) ? '#ffcccc' : undefined;
  const highlight = (fieldName) => prevAfter && isChanged(purchaseOrder[fieldName], prevAfter[fieldName]) ? { backgroundColor: '#fff3cd' } : {};
  const resolvedItems = filteredItems ?? computedItems ?? items;
  const displayItems = !isEmpty(resolvedItems) ? resolvedItems : [{}];
  const noteCell = (fieldName, content, style = {}) => {
    const cellKey = `po__${id}__${fieldName}`;
    const hasNote = !isEmpty(cellNotesById[cellKey]?.body);
    return (
      <td rowSpan={displayItems.length} className="position-relative has-hovered-contents" style={style}>
        {user && (
          <AdminHelp
            collectionName="cellNotes"
            helpKey={cellKey}
            user={user}
            title="メモ"
            label={false}
            disabledPreview
            buttonProps={{
              className: classnames('position-absolute p-0', { 'hovered-content': !hasNote }),
              style: { top: 1, left: 2, },
            }}
            icon="sticky-note"
          />
        )}
        {content}
      </td>
    );
  };

  return (
    <Fragment>
      {displayItems.map((item, i) => {
        const { productId, quantity, containerItems, leftQuantity, } = item;
        const product = productsById[productId];
        return (
          <tr key={i}>
            {i === 0 && (
              <Fragment>
                <td rowSpan={displayItems.length} className="bg-white sticky" style={{ lineHeight: 1.2, minWidth: labelColWidth, maxWidth: labelColWidth }}>
                  {labelCell}
                </td>
                <td rowSpan={displayItems.length} className="bg-white sticky" style={{ left: labelColWidth }}>{poNumber}</td>
                <td rowSpan={displayItems.length} style={highlight('supplierId')}>{supplier?.name}</td>
                {noteCell('orderedOn', orderedOn && formatDate(orderedOn.toDate(), 'yyyy/MM/dd'), highlight('orderedOn'))}
                {noteCell('piReceivedOn', piReceivedOn && formatDate(piReceivedOn.toDate(), 'yyyy/MM/dd'), { color: dateColor('piReceivedOn'), backgroundColor: alertBg('piReceivedOn'), ...highlight('piReceivedOn') })}
                {noteCell('advancePaymentOn', advancePaymentOn && formatDate(advancePaymentOn.toDate(), 'yyyy/MM/dd'), { color: dateColor('advancePaymentOn'), backgroundColor: alertBg('advancePaymentOn'), ...highlight('advancePaymentOn') })}
                {noteCell('scheduledManufactureDate', scheduledManufactureDate && formatDate(scheduledManufactureDate.toDate(), 'yyyy/MM/dd'), { color: dateColor('scheduledManufactureDate'), backgroundColor: alertBg('scheduledManufactureDate'), ...highlight('scheduledManufactureDate') })}
                {noteCell('colorSpecificationDate', colorSpecificationDate && formatDate(colorSpecificationDate.toDate(), 'yyyy/MM/dd'), { color: dateColor('colorSpecificationDate'), backgroundColor: alertBg('colorSpecificationDate'), ...highlight('colorSpecificationDate') })}
                {noteCell('tireArrivalDate', tireArrivalDate && formatDate(tireArrivalDate.toDate(), 'yyyy/MM/dd'), { color: dateColor('tireArrivalDate'), backgroundColor: alertBg('tireArrivalDate'), ...highlight('tireArrivalDate') })}
                {noteCell('frameArrivalDate', frameArrivalDate && formatDate(frameArrivalDate.toDate(), 'yyyy/MM/dd'), { color: dateColor('frameArrivalDate'), backgroundColor: alertBg('frameArrivalDate'), ...highlight('frameArrivalDate') })}
                {noteCell('otherPartsArrivalDate', otherPartsArrivalDate && formatDate(otherPartsArrivalDate.toDate(), 'yyyy/MM/dd'), { color: dateColor('otherPartsArrivalDate'), backgroundColor: alertBg('otherPartsArrivalDate'), ...highlight('otherPartsArrivalDate') })}
                {noteCell('completedOn', completedOn && formatDate(completedOn.toDate(), 'yyyy/MM/dd'), { color: dateColor('completedOn'), backgroundColor: alertBg('completedOn'), ...highlight('completedOn') })}
                {noteCell('onlineInspectionDate', onlineInspectionDate && formatDate(onlineInspectionDate.toDate(), 'yyyy/MM/dd'), { color: dateColor('onlineInspectionDate'), backgroundColor: alertBg('onlineInspectionDate'), ...highlight('onlineInspectionDate') })}
                {noteCell(
                  'onlineInspectionResult',
                  <div style={{ position: 'absolute', inset: 0, whiteSpace: 'pre-line', overflow: 'auto', padding: 4 }}>{onlineInspectionResult}</div>,
                  highlight('onlineInspectionResult'),
                )}
                {noteCell('balancePaymentOn', balancePaymentOn && formatDate(balancePaymentOn.toDate(), 'yyyy/MM/dd'), { color: dateColor('balancePaymentOn'), backgroundColor: alertBg('balancePaymentOn'), ...highlight('balancePaymentOn') })}
                {noteCell('requestEtd', requestEtd && formatDate(requestEtd.toDate(), 'yyyy/MM/dd'), { color: dateColor('requestEtd'), backgroundColor: alertBg('requestEtd'), ...highlight('requestEtd') })}
                <td rowSpan={displayItems.length} style={{ position: 'relative', ...highlight('info') }}><div style={{ position: 'absolute', inset: 0, whiteSpace: 'pre-line', overflow: 'auto', padding: 4 }}>{info}</div></td>
                <td rowSpan={displayItems.length} style={{ position: 'relative', ...highlight('note') }}><div style={{ position: 'absolute', inset: 0, whiteSpace: 'pre-line', overflow: 'auto', padding: 4 }}>{note}</div></td>
              </Fragment>
            )}
            <td style={highlight('items')}>{product?.code}</td>
            <td className="text-right" style={{ ...highlight('items'), ...(!confirmedFields.completedOn && { color: '#28a745' }) }}>{quantity != null && numeral(quantity).format()}</td>
            <td style={highlight('items')}>
              {containerItems?.map((containerItem, i) => (
                <div key={i} className="d-flex justify-content-between gap-2">
                  <div>{containerItem.container.number}</div>
                  <div>{numeral(containerItem.quantity).format()}</div>
                </div>
              ))}
            </td>
            <td className="text-right" style={{ ...highlight('items'), ...(!confirmedFields.completedOn && { color: '#28a745' }) }}>{leftQuantity != null && numeral(leftQuantity).format()}</td>
          </tr>
        );
      })}
    </Fragment>
  );
}
